import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';
import { isAdminUser } from '../utils/isAdminUser';

/**
 * The Message Centre: what the integralDelta team has to say, in two tabs.
 *
 * <h2>Read-only for a customer, editable for an admin — one component, not two</h2>
 *
 * The same box either way, with `readOnly` flipped. Two components would be two renderings of the
 * same content that could disagree about what a customer sees, and the customer's view is the one
 * that matters: an admin who edits in a different box from the one everybody reads is not looking at
 * what they are publishing.
 *
 * <h2>The Publish button is a CONVENIENCE, not the permission</h2>
 *
 * `canPublish` comes from the server, on the same response as the messages, and the POST re-checks it
 * on its own authority. Hiding a button stops nobody with a terminal, so the endpoint is where the
 * rule lives — see `MessageController`. This flag exists so a standard user is not shown a control
 * that would 403, and it comes from the server rather than from the local session because the server
 * is the thing that will decide.
 *
 * <p>The local role is read too, and the two are OR'd. A genuine admin can arrive with no
 * `subscriptionType` at all when the profile fetch fell back (see `utils/isAdminUser`), and the
 * reverse blind spot is real as well — so either witness offers the editor, and the POST settles it.
 *
 * <h2>Plain text, deliberately</h2>
 *
 * A multiline TextField, rendered as text. This is the one place in the product where one user's
 * input is displayed to every other user, which is precisely the shape of a stored XSS hole. Nothing
 * about a customer notice needs markup enough to earn that.
 *
 * <h2>The draft is discarded on close, and the load is per-opening</h2>
 *
 * Opening fetches; closing keeps nothing. A notice held in component state across openings would
 * show an admin the text they typed last time as though it were published, and would re-publish it on
 * the next Save.
 */

const PUBLIC = 'public';
const PRIVATE = 'private';

const EMPTY = { body: '', updatedAt: '', updatedBy: '', recipient: '' };

/** When a notice was last set, in the reader's own locale. Empty when it never was. */
const describeStamp = (msg) => {
  if (!msg || !msg.updatedAt) return '';
  const when = new Date(msg.updatedAt);
  const stamp = Number.isNaN(when.getTime()) ? msg.updatedAt : when.toLocaleString();
  return msg.updatedBy ? `Last updated ${stamp} by ${msg.updatedBy}` : `Last updated ${stamp}`;
};

const MessagesDialog = ({ open, onClose }) => {
  const { api, isAdmin, user } = useAuth();

  const [tab, setTab] = useState(PUBLIC);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [serverCanPublish, setServerCanPublish] = useState(false);
  const [addresses, setAddresses] = useState({});
  const [recipient, setRecipient] = useState('');

  // What the server last told us, kept separately from the draft so Reset has something to go back
  // to and so a failed save does not leave the screen claiming a value nobody stored.
  const [loaded, setLoaded] = useState({ [PUBLIC]: EMPTY, [PRIVATE]: EMPTY });
  const [draft, setDraft] = useState({ [PUBLIC]: '', [PRIVATE]: '' });

  const canPublish = serverCanPublish || isAdminUser(isAdmin, user);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const res = await api.get('/api/messages');
      const data = res.data || {};
      const pub = data.public || EMPTY;
      const priv = data.private || EMPTY;
      setLoaded({ [PUBLIC]: pub, [PRIVATE]: priv });
      setDraft({ [PUBLIC]: pub.body || '', [PRIVATE]: priv.body || '' });
      setServerCanPublish(data.canPublish === true);
      const addr = data.addresses || {};
      setAddresses(addr);
      // Default to the customer themselves. Their own address is the narrower of the two, so an
      // admin who does not change it cannot accidentally address a whole company.
      setRecipient(priv.recipient || addr.user || addr.company || '');
    } catch (e) {
      // The dialog opens either way, with the error stated. "We could not load your messages" and
      // "there are no messages" are different facts and must not look alike — which is exactly why
      // the server sends an empty body rather than an error when there is nothing to say.
      setError(e?.response?.data?.message || e?.message
        || 'Could not load messages. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const active = loaded[tab] || EMPTY;
  const dirty = (draft[tab] || '') !== (active.body || '');

  const addressOptions = useMemo(() => {
    const out = [];
    if (addresses.user) out.push({ value: addresses.user, label: `Just ${addresses.user}` });
    if (addresses.company) {
      out.push({ value: addresses.company, label: `Everyone at ${addresses.company}` });
    }
    // A recipient already on the stored message that is neither of the above — an address for
    // another customer, typed in a previous session. Kept so an admin editing that message does not
    // silently retarget it at whoever happens to be signed in.
    if (recipient && !out.some(o => o.value === recipient)) {
      out.unshift({ value: recipient, label: recipient });
    }
    return out;
  }, [addresses, recipient]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = { type: tab, body: draft[tab] || '' };
      if (tab === PRIVATE) payload.recipient = recipient;
      const res = await api.post('/api/messages', payload);
      const saved = (res.data && res.data.message) || { ...EMPTY, body: payload.body };
      setLoaded(prev => ({ ...prev, [tab]: saved }));
      setDraft(prev => ({ ...prev, [tab]: saved.body || '' }));
      setNotice(tab === PUBLIC
        ? 'Published to everyone.'
        : `Published to ${saved.recipient || recipient}.`);
    } catch (e) {
      // The server's own reason, when it gave one. A 403 here means the client thought this user
      // could publish and the server disagreed, and the customer needs to read which.
      setError(e?.response?.data?.message || e?.response?.data?.error || e?.message
        || 'Could not publish that message.');
    } finally {
      setSaving(false);
    }
  };

  const stamp = describeStamp(active);
  const nothingToShow = !loading && !error && !(active.body || '').trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Messages</DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value={PUBLIC} label="Public" sx={{ textTransform: 'none' }} />
        <Tab value={PRIVATE} label="Private" sx={{ textTransform: 'none' }} />
      </Tabs>

      <DialogContent>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading messages…</Typography>
          </Stack>
        )}

        {!loading && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tab === PUBLIC
                ? 'Announcements from the integralDelta team to every subscriber.'
                : 'Messages from the integralDelta team addressed to you.'}
            </Typography>

            {canPublish && tab === PRIVATE && (
              <TextField
                select={addressOptions.length > 0}
                fullWidth
                size="small"
                label="Addressed to"
                value={recipient}
                onChange={e => setRecipient(e.target.value)}
                helperText="A customer's email address, or @company.com for everyone there."
                sx={{ mb: 2 }}
              >
                {addressOptions.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              multiline
              minRows={8}
              maxRows={16}
              value={draft[tab] || ''}
              onChange={e => setDraft(prev => ({ ...prev, [tab]: e.target.value }))}
              // readOnly rather than disabled: a standard user must still be able to select the
              // text, scroll it and copy it. `disabled` greys it out and blocks all three, which
              // turns "this is not yours to edit" into "this is not yours to read".
              InputProps={{ readOnly: !canPublish }}
              placeholder={canPublish
                ? 'Write the message customers will see…'
                : 'There are no messages here yet.'}
              inputProps={{ 'aria-label': `${tab} message`, 'aria-readonly': !canPublish }}
            />

            <Box sx={{ mt: 1, minHeight: 20 }}>
              {stamp && (
                <Typography variant="caption" color="text.secondary">{stamp}</Typography>
              )}
              {!stamp && nothingToShow && !canPublish && (
                <Typography variant="caption" color="text.secondary">
                  Nothing published yet.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {canPublish && (
          <Button
            onClick={() => setDraft(prev => ({ ...prev, [tab]: active.body || '' }))}
            disabled={!dirty || saving}
            sx={{ mr: 'auto', textTransform: 'none' }}
          >
            Discard changes
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Close</Button>
        {canPublish && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading || (tab === PRIVATE && !recipient.trim())}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Publishing…' : 'Save / Publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

MessagesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MessagesDialog;
